// ============================================================
// addressLookup.ts
//
// Live Google Places integration on the New Places API
// (AutocompleteSuggestion + Place class). (E-sign audit fixes A)
// The Maps JS API is loaded LAZILY: loadGoogleMaps() injects the
// <script> on the first lookup (loading=async + callback), so the
// public e-signature pages never load a third-party script. The key comes from REACT_APP_GOOGLE_PLACES_KEY
// substituted at build time by CRA.
//
// PRECONDITION: Places API (New) must be enabled in the Cloud
// project. The legacy AutocompleteService / PlacesService that
// the previous version of this file used became unavailable to
// new Google Cloud customers on 1 March 2025, so we use the new
// promise-based classes instead.
//
// Public API (UNCHANGED — AddressLookup.tsx imports these):
//   AddressSuggestion           — { placeId, description }
//   fetchAddressSuggestions(q)  — UK-restricted address predictions
//   fetchAddressDetails(id)     — full 5-field Address for a placeId
//
// Cost-tier discipline:
//   - Suggestions: includedRegionCodes: ['gb'] for UK-only.
//     The includedPrimaryTypes restriction was dropped on purpose:
//     postcodes are categorised as 'postal_code' and were being
//     filtered out, so users couldn't search by postcode. Without
//     the restriction the natural ranking still surfaces address
//     results first for UK queries — businesses / regions rarely
//     pollute the dropdown in practice, and the cost-tier impact
//     is negligible at the firm's usage scale.
//   - Details: Place.fetchFields with fields restricted to
//     ['addressComponents', 'formattedAddress'] so we stay in the
//     cheapest Place Details Essentials SKU.
// ============================================================
import { Address } from '../types/case.types'

/** Suggestion row shown in the autocomplete dropdown. Shape
 *  unchanged from the previous version — consumers only see
 *  { placeId, description }. */
export interface AddressSuggestion {
  placeId: string
  description: string
}

// ── Minimal type surface for the New Places API classes we touch ──
// Co-located with the only code that uses them, intentionally
// narrow. Reference docs:
//   https://developers.google.com/maps/documentation/javascript/reference/autocomplete-data
//   https://developers.google.com/maps/documentation/javascript/reference/place
//
// Note: address-component TYPE values like 'street_number',
// 'postal_town', etc. are still snake_case strings — those are
// Google's stable enum values. Only the wrapping FIELD NAMES
// (longText, shortText, addressComponents, formattedAddress)
// switched to camelCase in the new API.

interface FormattableText {
  text: string
}

interface PlacePrediction {
  placeId: string
  text: FormattableText
}

interface AutocompleteSuggestionInstance {
  placePrediction?: PlacePrediction
}

interface AutocompleteResponse {
  suggestions: AutocompleteSuggestionInstance[]
}

interface AutocompleteRequest {
  input: string
  includedRegionCodes?: string[]
}

interface AddressComponent {
  types: string[]
  longText: string
  shortText: string
}

interface PlaceInstance {
  fetchFields(request: { fields: string[] }): Promise<unknown>
  readonly addressComponents?: AddressComponent[]
  readonly formattedAddress?: string
}

interface AutocompleteSuggestionStatic {
  fetchAutocompleteSuggestions(
    request: AutocompleteRequest,
  ): Promise<AutocompleteResponse>
}

declare global {
  interface Window {
    google?: {
      maps: {
        places: {
          AutocompleteSuggestion: AutocompleteSuggestionStatic
          Place: new (options: { id: string }) => PlaceInstance
        }
      }
    }
  }
}

// (E-sign audit fixes A) Inject the Maps script once, on first use; every
// caller waits for the same promise. A failed load can be retried.
const MAPS_CALLBACK = '__casevoMapsReady'
let mapsLoading: Promise<void> | null = null
export const loadGoogleMaps = (): Promise<void> => {
  if (window.google?.maps?.places) return Promise.resolve()
  if (mapsLoading) return mapsLoading
  mapsLoading = new Promise<void>((resolve, reject) => {
    const key = process.env.REACT_APP_GOOGLE_PLACES_KEY ?? ''
    ;(window as unknown as Record<string, unknown>)[MAPS_CALLBACK] = () => resolve()
    const s = document.createElement('script')
    s.async = true
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&loading=async&libraries=places&callback=${MAPS_CALLBACK}`
    s.onerror = () => { mapsLoading = null; s.remove(); reject(new Error('Address lookup service not loaded — refresh the page')) }
    document.head.appendChild(s)
  })
  return mapsLoading
}

const assertGoogleLoaded = (): NonNullable<typeof window.google> => {
  const g = window.google
  if (
    !g ||
    !g.maps ||
    !g.maps.places ||
    !g.maps.places.AutocompleteSuggestion ||
    !g.maps.places.Place
  ) {
    throw new Error('Address lookup service not loaded — refresh the page')
  }
  return g
}

export const fetchAddressSuggestions = async (
  query: string,
): Promise<AddressSuggestion[]> => {
  const q = query.trim()
  if (!q) return []
  await loadGoogleMaps()
  const g = assertGoogleLoaded()
  // Session tokens not implemented — would halve per-session
  // billing but require module-scope state (a session token plus
  // a predictions cache keyed by placeId so fetchAddressDetails
  // could reuse the original PlacePrediction's toPlace()
  // attribution). Worth ~pennies/month at current usage; revisit
  // if traffic scales 100x.
  try {
    const { suggestions } =
      await g.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: q,
        includedRegionCodes: ['gb'],
      })
    // The new API surfaces "no matches" as an empty suggestions
    // array — no ZERO_RESULTS status to check. Empty → []; not an
    // error.
    return suggestions
      .map((s) => s.placePrediction)
      .filter((p): p is PlacePrediction => !!p)
      .map((p) => ({
        placeId: p.placeId,
        description: p.text.text,
      }))
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err)
    throw new Error('Address lookup failed (' + message + ')')
  }
}

// Pulls the first component matching a given type out of the
// addressComponents array. Returns '' (not undefined) so callers
// can assign straight into the 5-string Address shape. The type
// VALUES ('street_number', 'postal_town', etc.) are still snake_
// case in the new API — only the wrapping field names changed.
const pickComponent = (
  components: AddressComponent[],
  type: string,
): string => {
  const match = components.find((c) => c.types.includes(type))
  return match ? match.longText : ''
}

export const fetchAddressDetails = async (
  placeId: string,
): Promise<Address> => {
  await loadGoogleMaps()
  const g = assertGoogleLoaded()
  const place = new g.maps.places.Place({ id: placeId })
  try {
    // Cheapest Place Details SKU — every additional field bumps
    // the cost tier. addressComponents is all we structurally
    // need; formattedAddress is kept for human-readable logs and
    // ad-hoc debugging.
    await place.fetchFields({
      fields: ['addressComponents', 'formattedAddress'],
    })
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err)
    throw new Error('Address details failed (' + message + ')')
  }
  const components = place.addressComponents ?? []
  const streetNumber = pickComponent(components, 'street_number')
  const route = pickComponent(components, 'route')
  // line1 joins number + street; trim handles the "no number"
  // case (route alone). line2 stays '' because Google doesn't
  // typically split flat/unit numbers — users edit the manual
  // input below the autocomplete to add those.
  const line1 = [streetNumber, route].filter(Boolean).join(' ').trim()
  // postal_town is the canonical UK town concept (e.g.
  // "Manchester", "London"). locality is the fallback for the
  // small share of UK results where postal_town isn't set.
  const town =
    pickComponent(components, 'postal_town') ||
    pickComponent(components, 'locality')
  // administrative_area_level_2 is the county equivalent in the
  // UK ("Greater Manchester"). level_1 is region ("England") —
  // last-ditch fallback for unusual rows.
  const county =
    pickComponent(components, 'administrative_area_level_2') ||
    pickComponent(components, 'administrative_area_level_1')
  const postcode = pickComponent(components, 'postal_code')
  return {
    line1,
    line2: '',
    town,
    county,
    postcode,
  }
}
